import { useState, useEffect } from 'react';
import AppInfoApiClient from '../../api/appInfoApiClient';
import { FRONTEND_VERSION } from '../../version';

// The sidebar is mounted twice (temporary + permanent drawer), so the app-info call is
// cached at module level and both instances share the same request.
let cachedBackendVersion = null;
let inFlightAppInfoRequest = null;

const loadBackendVersion = () => {
    if (cachedBackendVersion) {
        return Promise.resolve(cachedBackendVersion);
    }

    if (!inFlightAppInfoRequest) {
        inFlightAppInfoRequest = AppInfoApiClient.getAppInfo()
            .then((appInfo) => {
                cachedBackendVersion = appInfo.backendVersion || 'v0.0.0';
                return cachedBackendVersion;
            })
            .finally(() => {
                inFlightAppInfoRequest = null;
            });
    }

    return inFlightAppInfoRequest;
};

/**
 * Custom hook for managing application version information
 * Fetches backend version from API and gets frontend version from version constant
 * @returns {Object} { backendVersion, frontendVersion, loading, error }
 */
const useAppVersion = () => {
    const [backendVersion, setBackendVersion] = useState(cachedBackendVersion);
    const [loading, setLoading] = useState(!cachedBackendVersion);
    const [error, setError] = useState(null);

    // Get frontend version from version constant
    const rawVersion = FRONTEND_VERSION || '0.0.0';
    const frontendVersion = rawVersion.startsWith('v') ? rawVersion : `v${rawVersion}`;

    useEffect(() => {
        let cancelled = false;

        loadBackendVersion()
            .then((version) => {
                if (!cancelled) {
                    setBackendVersion(version);
                }
            })
            .catch((err) => {
                console.error('Error fetching app info:', err);
                if (!cancelled) {
                    setError(err.message || 'Failed to load version information');
                    // Set a fallback version on error
                    setBackendVersion('v0.0.0');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return {
        backendVersion: backendVersion || 'v0.0.0',
        frontendVersion,
        loading,
        error
    };
};

export default useAppVersion;
